import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, XCircleIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
const faqs = [{
  question: 'Are there long-term contracts?',
  answer: 'No. All our packages are month-to-month. We believe in earning your business every month, not locking you in. You can cancel anytime with 30 days notice.'
}, {
  question: 'Is the ad budget included in the monthly fee?',
  answer: "No, your ad spend is separate from our management fee. This gives you full control over your budget. We'll recommend a starting budget based on your market and goals."
}, {
  question: 'How often do I get reports?',
  answer: 'All clients receive monthly performance reports. Full Automation Suite clients also get monthly strategy calls to review results and plan next steps.'
}, {
  question: 'What kind of support do I get?',
  answer: 'All clients get email and chat support during business hours. Never Miss A Lead and Full Automation Suite clients get priority response times.'
}, {
  question: 'How quickly can I get started?',
  answer: 'Most clients are fully set up within 5-7 business days. We handle all the technical setup so you can focus on running your business.'
}, {
  question: 'Do I need to be tech-savvy?',
  answer: "Not at all. We handle everything technical. You'll get a simple dashboard and mobile app to manage your leads and appointments."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-2228" data-mp="havmyw0yyr0k">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-2229" data-mp="ccalyudw83b2">
        <span className="text-lg font-semibold text-gray-900" data-id="element-2230" data-mp="1220x7ku318oa">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-2231" data-mp="161jfe8uir5nf">
          <ChevronDownIcon className="w-5 h-5" data-id="element-2232" data-mp="1fx30s1bgilxx" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-2233" data-mp="1kwfwlbkb33cn">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2234" data-mp="1ozc7sh7inx6k">
            <p className="pb-6 text-gray-600 leading-relaxed" data-id="element-2235" data-mp="28ocyoaxyeh0k">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function PricingPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-2236" data-mp="3o5hllvqky1nz">
      <SEOHead title="Pricing | Marketing Packages for Local Businesses | Viridia Analytics Phoenix" description="Transparent, month-to-month pricing for ad campaign management, AI lead conversion, and web design. No long-term contracts. Packages starting for Phoenix local service businesses." canonical="/pricing" keywords="marketing agency pricing, ad management pricing Phoenix, marketing automation cost, web design pricing, local business marketing packages, marketing agency phoenix, AI marketing agency, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-2237" data-mp="3j7g2jat5m2ci" />
      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-2238" data-mp="yjtp0dz1zw3u">
        <ScrollReveal data-id="element-2239" data-mp="tppexluhr7yj">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2240" data-mp="tp79bylxvhdy">
            CRM & AUTOMATION
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-2241" data-mp="4bcoirtji43g">
            Systems That Book Real Jobs -{' '}
            <span className="text-viridia" data-id="element-2242" data-mp="9a63ypse0569">Pick the One That Fits</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-2243" data-mp="x00eh0p8hhnc">
            Month-to-month systems that capture leads, automate follow-up, and
            keep your business organized - so you can focus on the work.
          </p>
        </ScrollReveal>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2244" data-mp="s6yy0o9dpa1n">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-2245" data-mp="1fxbc6gv7w30o">
          {/* Card 1: Business Essentials */}
          <ScrollReveal delay={0.1} data-id="element-2246" data-mp="1kqdyq51kuc7d">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-2247" data-mp="35f8a4nc6vhda">
              <div className="mb-8" data-id="element-2248" data-mp="30eaqs7fg0ha7">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2249" data-mp="3f5t8qarv4lw7">
                  Business Essentials
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-2250" data-mp="3a7j8vmv70x49">
                  Best for: Businesses that need a CRM foundation, scheduling,
                  and basic lead capture.
                </p>
              </div>
              <div className="mb-8" data-id="element-2251" data-mp="2hjdncxwbf43d">
                <span className="text-5xl font-extrabold text-white" data-id="element-2252" data-mp="2vh2bceloircn">$497</span>
                <span className="text-gray-500 font-medium" data-id="element-2253" data-mp="2vezbl2uq5juq">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-2254" data-mp="2rhtdoyniq9bt">
                {['CRM Setup & Contact Management', 'Online Booking / Calendar Integration', 'Professional Branded Invoicing & Revenue Tracking (PayPal / Square / Stripe sync)', 'QuickBooks & Local Billing Integration', 'Lead Capture Workflows', 'Lead Platform Auto-Responder (1 auto-response per new lead)', 'GHL Mobile App Access'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2255" data-mp="3dig3zkibrveg" data-mpi={i}>
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2256" data-mp="2gtqlbryviyrf" />
                    <span className="text-gray-300 text-sm" data-id="element-2257" data-mp="2lsbnvy1lluj1">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-2258" data-mp="2pt3v6lqwtpco">
                <p className="text-sm text-gray-400 mb-2" data-id="element-2259" data-mp="2ulx29g9up9rx">
                  TFP Landscaping runs on this package - 284% more leads, 28
                  AI-booked estimates in 30 days.
                </p>
                <Link to="/case-studies/tfp-landscaping" className="text-viridia text-sm font-semibold hover:text-viridia-dark flex items-center" data-id="element-2260" data-mp="223j2vc8v9fqq">
                  See the proof <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2261" data-mp="1pmi1kozptuxh" />
                </Link>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-2262" data-mp="1litsu2zcktan">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 2: Never Miss A Lead (MOST POPULAR) */}
          <ScrollReveal delay={0.2} data-id="element-2263" data-mp="11xfjjg16602g">
            <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-2264" data-mp="11ubb26go8i5z">
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-2265" data-mp="sv6cj0540ott">
                Most Popular
              </div>
              <div className="mb-8 mt-2" data-id="element-2266" data-mp="2gsf91vunf2id">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2267" data-mp="2grckvvopne65">
                  Never Miss A Lead
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-2268" data-mp="22scuojljur7v">
                  Best for: Busy contractors who lose jobs when calls go
                  unanswered.
                </p>
              </div>
              <div className="mb-8" data-id="element-2269" data-mp="1t1i6vasnnjkb">
                <span className="text-5xl font-extrabold text-white" data-id="element-2270" data-mp="1xt9ogvtu2eyb">$897</span>
                <span className="text-gray-500 font-medium" data-id="element-2271" data-mp="1xve2w7pcywcu">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-2272" data-mp="1k01y17hhnpi7">
                <li className="flex items-start" data-id="element-2273" data-mp="1o4rt4u4bp022">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2274" data-mp="1o1do1e49ycqx" />
                  <span className="text-white font-medium text-sm" data-id="element-2275" data-mp="1a6bbi84x9p55">
                    Everything in Business Essentials
                  </span>
                </li>
                {['AI Voice Receptionist (24/7 call answering)', 'Missed Call Text-Back Automation', 'A2P Registration & Business Number Setup', 'Lead Platform AI Conversational Chatbot', 'Unified Communications Hub'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2276" data-mp="1efumboijid0x" data-mpi={i}>
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2277" data-mp="1eeb61xwvw1z5" />
                    <span className="text-gray-300 text-sm" data-id="element-2278" data-mp="19grg40qtq0rd">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-2279" data-mp="1j6ug4kwd4ez7">
                <p className="text-sm text-gray-400 mb-2" data-id="element-2280" data-mp="1x6lqmbno7d8g">
                  Finish Coat Painting used this package to book 29 AI estimates
                  and close $15.05k in 30 days.
                </p>
                <Link to="/case-studies/finish-coat-painting" className="text-viridia text-sm font-semibold hover:text-viridia-dark flex items-center" data-id="element-2281" data-mp="1703e3t04zzjk">
                  See the proof <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2282" data-mp="x9sak7kd7sd7" />
                </Link>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25" data-id="element-2283" data-mp="sc8m6etydz7j">
                Get Started
              </Link>
            </div>
          </ScrollReveal>

          {/* Card 3: Full Automation Suite */}
          <ScrollReveal delay={0.3} data-id="element-2284" data-mp="1bzzi3s8u3qz4">
            <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-2285" data-mp="1bywtjqyzi0md">
              <div className="mb-8" data-id="element-2286" data-mp="j85sxdwoxki">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2287" data-mp="izuvb1cg2by">
                  Full Automation Suite
                </h3>
                <p className="text-gray-400 text-sm h-10" data-id="element-2288" data-mp="2g982rnovl2ag">
                  Best for: Businesses ready for the complete system. Your
                  business runs on autopilot.
                </p>
              </div>
              <div className="mb-8" data-id="element-2289" data-mp="2pzkqtpvwfdlc">
                <span className="text-5xl font-extrabold text-white" data-id="element-2290" data-mp="2q0m9aff8v2d3">
                  $1,497
                </span>
                <span className="text-gray-500 font-medium" data-id="element-2291" data-mp="2v08j0evv60h5">/month</span>
              </div>
              <ul className="space-y-4 mb-8 flex-grow" data-id="element-2292" data-mp="2zzbq04vemgrr">
                <li className="flex items-start" data-id="element-2293" data-mp="33ykfuynbpui1">
                  <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2294" data-mp="33wzo1gse8pnv" />
                  <span className="text-white font-medium text-sm" data-id="element-2295" data-mp="38vt0d5vy2amg">
                    Everything in Never Miss A Lead
                  </span>
                </li>
                {['AI Conversational Chatbot (all applicable platforms & website)', 'Reputation Management', 'Review Automation', 'Pipeline Automation', 'Monthly Strategy Calls'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2296" data-mp="2qx1dbu2mpc08" data-mpi={i}>
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2297" data-mp="2lvvklw8yxoqp" />
                    <span className="text-gray-300 text-sm" data-id="element-2298" data-mp="30t4diudx70kj">{feature}</span>
                  </li>)}
              </ul>
              <div className="mb-6 p-4 bg-viridia-black rounded-lg border border-gray-800" data-id="element-2299" data-mp="2vu1m4cqv4sv1">
                <p className="text-sm text-gray-400" data-id="element-2300" data-mp="39koabayzz0cv">
                  Includes everything that powered both case study results, plus
                  reputation management and monthly strategy calls.
                </p>
              </div>
              <Link to="/contact" className="w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800" data-id="element-2301" data-mp="3jht0dlvyrfyy">
                Get Started
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* AD MANAGEMENT ADD-ON */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2302" data-mp="3oar9ueefxanj">
        <ScrollReveal direction="up" data-id="element-2303" data-mp="3t6ga6jyaavkw">
          <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-2304" data-mp="3t9kpacdafbqc">
            <div data-id="element-2305" data-mp="3k7uz19v56iu5">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2306" data-mp="3k8e43pzp1cqr">
                Need Ad Management Too?
              </h3>
              <p className="text-gray-400 max-w-2xl" data-id="element-2307" data-mp="35gcc0s3v7o1a">
                Ad campaign management can be added to any package. Pricing
                depends on platforms, ad spend, and scope. Book a call to get a
                custom quote.
              </p>
            </div>
            <Link to="/contact" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-2308" data-mp="30iaptyb578ll">
              Get a Custom Ad Quote <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2309" data-mp="2oj5fjbzg4s82" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* WEBSITE PRICING SECTION */}
      <section id="web-design" className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2310" data-mp="2eqgezc5foyri">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2311" data-mp="2anbeff4wz75i">
          <ScrollReveal data-id="element-2312" data-mp="25p9zn6eittzu">
            <div className="text-center mb-16" data-id="element-2313" data-mp="25p0658u9p4pe">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2314" data-mp="20qoway3z2u76">
                WEB DESIGN & SEO
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-2315" data-mp="3v4ptlejvj955">
                A.I. Powered Websites Built to Convert
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-2316" data-mp="2iawqv6a44ny">
                High-converting, mobile-first websites built for local service
                businesses. One-time build, plus $30/month hosting.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center" data-id="element-2317" data-mp="bcfl97xxx40b">
            {/* Starter Site */}
            <ScrollReveal delay={0.1} data-id="element-2318" data-mp="3fokpwrwlwxqb">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-2319" data-mp="3fpmdtwyq1epg">
                <div className="mb-8" data-id="element-2320" data-mp="3oopxb21svw4h">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2321" data-mp="3onobq3n5rr8i">
                    Starter Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-2322" data-mp="13yquf6zm4d8q">
                    The essentials to get your business online and looking
                    professional.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2323" data-mp="j9fvd4ryy9bw">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2324" data-mp="j8egsqbqv4pf">
                    $500
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2325" data-mp="ecpfoik2tfr0">
                    one-time
                  </span>
                  <p className="text-viridia text-sm font-medium mt-2" data-id="element-2326" data-mp="o8sscick0ws7">
                    + $30/month hosting
                  </p>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-2327" data-mp="jzq645c9sp5">
                  {['A.I. powered, conversion-focused build', '3 pages (Home, Services, Contact)', 'Mobile responsive', 'Contact form', 'On-page SEO'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2328" data-mp="3dxuu1g44ppnh" data-mpi={i}>
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2329" data-mp="3dw9ufc2yjgir" />
                      <span className="text-gray-300 text-sm" data-id="element-2330" data-mp="38wxbh2f7itlg">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-2331" data-mp="1t87iifp2ct8e">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-2332" data-mp="1t9a6ofv04hkm">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-2333" data-mp="275dy61ehqbte">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Growth Site (MOST POPULAR) */}
            <ScrollReveal delay={0.2} data-id="element-2334" data-mp="227m6n8hrcrpq">
              <div className="bg-viridia-surface rounded-2xl p-8 border-2 border-viridia relative transform lg:scale-105 shadow-2xl shadow-viridia/10 h-full flex flex-col z-10" data-id="element-2335" data-mp="226klucaxwqut">
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-viridia text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-2336" data-mp="qu07cui6p9qh">
                  Most Popular
                </div>
                <div className="mb-8 mt-2" data-id="element-2337" data-mp="19g3qmvzh0ix4">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2338" data-mp="19h51p0ghl5eu">
                    Growth Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-2339" data-mp="14iv7as2l68ua">
                    Built to convert traffic into leads and dominate local
                    search.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2340" data-mp="1x7spkv6ss5n1">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2341" data-mp="1seg0kjy7hwtr">
                    $850
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2342" data-mp="1netq2i9riuon">
                    one-time
                  </span>
                  <p className="text-viridia text-sm font-medium mt-2" data-id="element-2343" data-mp="2kwttc54oqslz">
                    + $30/month hosting
                  </p>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-2344" data-mp="2ng87jimdvfk6">
                  <li className="flex items-start" data-id="element-2345" data-mp="2nht5li7wpgms">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2346" data-mp="2se02ymm8ljt4" />
                    <span className="text-white font-medium text-sm" data-id="element-2347" data-mp="2xdmmtewklyaw">
                      Everything in Starter Site
                    </span>
                  </li>
                  {['5–7 pages', 'Custom design', 'Service-specific pages', 'Review/trust section', 'Conversion CTAs', 'On-page SEO baked in', 'Google Analytics setup'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2348" data-mp="3l2xi4gv5j0hn" data-mpi={i}>
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2349" data-mp="3p5kd2qn4d1j2" />
                      <span className="text-gray-300 text-sm" data-id="element-2350" data-mp="3u3ly3h43vax6">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-2351" data-mp="ywxxbgw1bh5q">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 bg-viridia hover:bg-viridia-dark text-white shadow-lg shadow-viridia/25 mb-3" data-id="element-2352" data-mp="13u6djbxmhb4c">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-400 text-xs" data-id="element-2353" data-mp="1ht64wrcis447">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            {/* Premium Site */}
            <ScrollReveal delay={0.3} data-id="element-2354" data-mp="1rm522xm312zf">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 hover:shadow-2xl hover:shadow-viridia/5 transition-all duration-300 h-full flex flex-col" data-id="element-2355" data-mp="1vm5ketcaxybr">
                <div className="mb-8" data-id="element-2356" data-mp="25jtfjkdtl7ug">
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2357" data-mp="25kl9kqrrqxim">
                    Premium Site
                  </h3>
                  <p className="text-gray-400 text-sm h-10" data-id="element-2358" data-mp="2fp1pfk590emf">
                    The ultimate digital storefront for established businesses.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2359" data-mp="25q2oknqq5n9z">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2360" data-mp="21nz0mzmvcq7t">
                    $1,200+
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2361" data-mp="2yerk8790h0ec">
                    one-time
                  </span>
                  <p className="text-viridia text-sm font-medium mt-2" data-id="element-2362" data-mp="2tgg8fqz5ofth">
                    + $30/month hosting
                  </p>
                </div>
                <ul className="space-y-4 mb-8 flex-grow" data-id="element-2363" data-mp="2kmti4gdpw4ro">
                  <li className="flex items-start" data-id="element-2364" data-mp="2kmbj5bzo7cg2">
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2365" data-mp="3h5989yiwf7td" />
                    <span className="text-white font-medium text-sm" data-id="element-2366" data-mp="3cdhezg8cynzj">
                      Everything in Growth Site
                    </span>
                  </li>
                  {['8–12 pages', 'Full custom design', 'Blog setup', 'Multiple service pages', 'Schema markup', 'Lead capture', 'Speed optimization'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2367" data-mp="rpg0wajiwu0r" data-mpi={i}>
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2368" data-mp="rs2643ixyxft" />
                      <span className="text-gray-300 text-sm" data-id="element-2369" data-mp="wmpnlifj2rkw">{feature}</span>
                    </li>)}
                </ul>
                <div className="mt-auto" data-id="element-2370" data-mp="mq3530aoccm1">
                  <Link to="/contact" className="block w-full py-3 px-4 rounded-lg font-semibold text-center transition-all duration-300 border border-gray-600 text-white hover:bg-gray-800 mb-3" data-id="element-2371" data-mp="mr4ru211ynjl">
                    Get a Quote
                  </Link>
                  <p className="text-center text-gray-500 text-xs" data-id="element-2372" data-mp="5f7mthdayqxe">
                    One-time fee. No hidden costs.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO ADD-ONS */}
      <section className="py-16 bg-viridia-black" data-id="element-2373" data-mp="afc6thdf63sd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2374" data-mp="o7iior9jhom0">
          <ScrollReveal data-id="element-2375" data-mp="o9wg36rozcwq">
            <div className="text-center mb-12" data-id="element-2376" data-mp="k6zqux7ncijt">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-2377" data-mp="k66rwiexixc8">
                Local SEO Add-Ons
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2378" data-mp="t74y97cubgxz">
                Ongoing optimization to ensure your new website actually gets
                found by local customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2379" data-mp="2uzjd1xk385a6">
            {/* Basic */}
            <ScrollReveal delay={0.1} data-id="element-2380" data-mp="2wen51vdwdu4w">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2381" data-mp="2whrhfg1inw6n">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2382" data-mp="31gkvpaomnr7v">
                  Basic Local SEO
                </h4>
                <div className="mb-6" data-id="element-2383" data-mp="3bcnvwazx5fs6">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2384" data-mp="3bbmnkeaaf7e5">
                    $300–$500
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2385" data-mp="2dn2q3w6otk9y">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2386" data-mp="2iojptodfr0oy">
                  {['GBP optimization', 'Citations', 'Monthly report'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2387" data-mp="2sev5g86heybo" data-mpi={i}>
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2388" data-mp="1uyxoovdi6beb" />
                        <span className="text-gray-300 text-sm" data-id="element-2389" data-mp="1zxitl83p9j93">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Standard */}
            <ScrollReveal delay={0.2} data-id="element-2390" data-mp="d2dt5het4o4c">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2391" data-mp="81zmo3sutm8f">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2392" data-mp="3355f6ajc5qf">
                  Standard Local SEO
                </h4>
                <div className="mb-6" data-id="element-2393" data-mp="3u9g3quo5use2">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2394" data-mp="3uahfz2gwxkxd">
                    $500–$800
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2395" data-mp="3lgvz9dl6ttlb">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2396" data-mp="3lei311erubc6">
                  <li className="flex items-start" data-id="element-2397" data-mp="3gj2rig48krix">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2398" data-mp="3biosfnkm4rx1" />
                    <span className="text-white font-medium text-sm" data-id="element-2399" data-mp="36jlns48jonpl">
                      Everything in Basic
                    </span>
                  </li>
                  {['On-page SEO', 'Schema markup', 'Review management'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2400" data-mp="31szsddspgj8s" data-mpi={i}>
                        <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2401" data-mp="2nwmleylss08k" />
                        <span className="text-gray-300 text-sm" data-id="element-2402" data-mp="2rxod8uddxa0i">{feature}</span>
                      </li>)}
                </ul>
              </div>
            </ScrollReveal>

            {/* Full */}
            <ScrollReveal delay={0.3} data-id="element-2403" data-mp="3knesx7uo0jyq">
              <div className="bg-viridia-surface rounded-xl p-6 border border-gray-800 h-full flex flex-col" data-id="element-2404" data-mp="36uyxas43fo7u">
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-2405" data-mp="3aupjsjml7i6k">
                  Full Local SEO
                </h4>
                <div className="mb-6" data-id="element-2406" data-mp="2eebplgjggnx">
                  <span className="text-2xl font-bold text-viridia" data-id="element-2407" data-mp="76nxqqej8wkx">
                    $800–$1,200
                  </span>
                  <span className="text-gray-500 text-sm" data-id="element-2408" data-mp="1rwb3fa6x2ys3">/mo</span>
                </div>
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-2409" data-mp="1myjfssdb1ytp">
                  <li className="flex items-start" data-id="element-2410" data-mp="1mvx5wm2xz78b">
                    <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2411" data-mp="21n5v4u2xmqkx" />
                    <span className="text-white font-medium text-sm" data-id="element-2412" data-mp="1wnjf6cvpyw8f">
                      Everything in Standard
                    </span>
                  </li>
                  {['Citation building', 'GBP posts & content', 'City pages', 'Competitor tracking'].map((feature, i) => <li key={i} className="flex items-start" data-id="element-2413" data-mp="1dws8xk713ly9" data-mpi={i}>
                      <CheckCircleIcon className="w-4 h-4 text-viridia mr-2 flex-shrink-0 mt-1" data-id="element-2414" data-mp="1dzo799msgcn8" />
                      <span className="text-gray-300 text-sm" data-id="element-2415" data-mp="2udedalxgxazi">{feature}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ONE-TIME LOCAL PRESENCE SERVICES */}
      <section className="py-16 bg-viridia-black border-t border-gray-800" data-id="element-2416" data-mp="rpfv4m04ojmi">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2417" data-mp="wmy246ul4orv">
          <ScrollReveal data-id="element-2418" data-mp="11efui6ijlh18">
            <div className="text-center mb-12" data-id="element-2419" data-mp="11fr8ez5pc562">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2420" data-mp="11githg1oaki3">
                ONE-TIME SERVICE
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-2421" data-mp="1fdyxkkwhy26o">
                Google Business Profile Launch & Verification
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-2422" data-mp="1kbh98j1wevid">
                If your Google Business Profile isn't set up or verified
                correctly, you may not be showing up where local customers
                search. We handle the setup and verification process so your
                business is properly listed, visible, and ready to support
                reviews, calls, and local trust.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1} data-id="element-2423" data-mp="34w49ynp8otwl">
            <div className="bg-viridia-surface rounded-2xl p-8 md:p-10 border border-gray-800" data-id="element-2424" data-mp="34t9t165fpr7x">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8" data-id="element-2425" data-mp="2zuooiuj5gldo">
                <div data-id="element-2426" data-mp="3sl70ym5rjz3r">
                  <h4 className="text-2xl font-bold text-white mb-1" data-id="element-2427" data-mp="3sknkzazobksd">
                    GBP Launch & Verification
                  </h4>
                  <p className="text-gray-400 text-sm" data-id="element-2428" data-mp="3nkj078rqg3wc">
                    A strong first step for businesses not yet properly set up
                    on Google. Pairs well with our automation, ad, and website
                    services.
                  </p>
                </div>
                <div className="flex-shrink-0 text-right" data-id="element-2429" data-mp="2772mvrthfqtg">
                  <span className="text-4xl font-extrabold text-white" data-id="element-2430" data-mp="15fy3w9ctedyw">
                    $397
                  </span>
                  <span className="text-gray-500 font-medium ml-2" data-id="element-2431" data-mp="10hwe75kvvgeg">
                    one-time
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-2432" data-mp="1afariwbifp2j">
                {['Create or claim your Google Business Profile', 'Guide or manage the verification process', 'Add correct business name, address & info', 'Set service area & local coverage correctly', 'Choose the right business categories', 'Add business description', 'Add website, phone, hours & core details', 'Upload logo & branded image placeholders (if assets available)', 'Final check that profile is live & properly configured'].map((feature, i) => <div key={i} className="flex items-start" data-id="element-2433" data-mp="2ael010bdkkqx" data-mpi={i}>
                    <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2434" data-mp="2ah7frn8f66k8" />
                    <span className="text-gray-300 text-sm" data-id="element-2435" data-mp="2fgaaadqrwudy">{feature}</span>
                  </div>)}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 pt-6 border-t border-gray-800" data-id="element-2436" data-mp="25nut3inaw9u4">
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 text-sm font-bold text-viridia-black bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 w-full sm:w-auto" data-id="element-2437" data-mp="1rodc3pg8y0vb">
                  Get Started
                  <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-2438" data-mp="1mqlag3psv0dx" />
                </Link>
                <p className="text-gray-500 text-xs text-center sm:text-left" data-id="element-2439" data-mp="3c3ykm9klfimy">
                  This is Viridia's service fee for setup and verification help.
                  Google does not charge for creating a Business Profile.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* HUBSPOT SEO CERTIFICATION */}
      <section className="py-16 bg-viridia-black" data-id="element-2440" data-mp="35nybuxlc2d5e">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2441" data-mp="35oq7g8exkavo">
          <ScrollReveal data-id="element-2442" data-mp="30pwtkevqehuz">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-2443" data-mp="30qew1t8zlsbc">
              <div className="flex-shrink-0 w-full md:w-auto flex justify-center" data-id="element-2444" data-mp="3te2f33um0avv">
                <div className="relative group" data-id="element-2445" data-mp="3tg53xkk0zxz0">
                  <div className="absolute -inset-1 bg-viridia/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2446" data-mp="3om0wz1arfpxx"></div>
                  <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot Academy SEO Certification - Kevin Rogers" loading="lazy" className="relative w-64 md:w-72 rounded-lg shadow-xl border border-gray-700" data-id="element-2447" data-mp="kxz7sz7eso82" />
                </div>
              </div>
              <div className="text-center md:text-left" data-id="element-2448" data-mp="xgl2h8r51lxl">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4" data-id="element-2449" data-mp="xf075gx01yyo">
                  <svg viewBox="0 0 512 512" className="w-8 h-8" aria-label="HubSpot logo" data-id="element-2450" data-mp="12gh6h7zu5dd5">
                    <path fill="#FF7A59" d="M380.48 256c0-19.2-9.6-36.48-24.96-47.04V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v40.32c-5.28-1.92-10.88-3.2-16.64-3.52V136c0-17.28-14.08-31.36-31.36-31.36-17.28 0-31.36 14.08-31.36 31.36v62.4c-5.76.32-11.36 1.6-16.64 3.52V161.6c0-7.68-6.24-13.92-13.92-13.92h-.96c-7.68 0-13.92 6.24-13.92 13.92v47.36C186.72 219.52 177.12 236.8 177.12 256c0 29.44 19.52 54.4 46.24 62.72l-24.64 24.64c-3.84 3.84-3.84 10.08 0 13.92 1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88l31.04-31.04c5.28 1.6 10.88 2.56 16.64 2.88v44.48c0 17.28 14.08 31.36 31.36 31.36 17.28 0 31.36-14.08 31.36-31.36v-44.48c5.76-.32 11.36-1.28 16.64-2.88l31.04 31.04c1.92 1.92 4.48 2.88 6.96 2.88s5.04-.96 6.96-2.88c3.84-3.84 3.84-10.08 0-13.92l-24.64-24.64C360.96 310.4 380.48 285.44 380.48 256zM278.72 296.96c-22.56 0-40.96-18.4-40.96-40.96s18.4-40.96 40.96-40.96 40.96 18.4 40.96 40.96-18.4 40.96-40.96 40.96z" data-id="element-2451" data-mp="1bfkqqfaqnyt8" />
                  </svg>
                  <span className="text-sm font-bold text-[#FF7A59] tracking-wide uppercase" data-id="element-2452" data-mp="23zcgp9ien0kv">
                    HubSpot Academy
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" data-id="element-2453" data-mp="2duw7xim0xb3a">
                  SEO Certified
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-2454" data-mp="3tawinvmxsqjs">
                  Our team is HubSpot SEO Certified - trained and tested on
                  technical SEO, on-page optimization, link building, and search
                  strategy. Not guesswork. Proven methodology.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FREELANCER COMPARISON */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-2455" data-mp="3rvkd03p6jsct">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2456" data-mp="3rxmzwev1d5db">
          <ScrollReveal data-id="element-2457" data-mp="jh1wrep356i4">
            <div className="text-center mb-12" data-id="element-2458" data-mp="jginemdi3qfp">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2459" data-mp="ofda3zel9z53">
                Why Not Just Hire a Freelancer?
              </h2>
              <p className="text-lg text-gray-400" data-id="element-2460" data-mp="9pnsktm6viin">
                You get what you pay for. We build systems that generate
                revenue, not just pretty brochures.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2461" data-mp="1751n4dihiuw6">
            <ScrollReveal delay={0.1} data-id="element-2462" data-mp="177ee1g680fld">
              <div className="bg-viridia-surface/50 rounded-2xl p-8 border border-red-900/30 h-full" data-id="element-2463" data-mp="1h1tpk7ix42s4">
                <h3 className="text-xl font-bold text-gray-300 mb-6 flex items-center" data-id="element-2464" data-mp="1c1p9gilx9a2f">
                  <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center mr-3" data-id="element-2465" data-mp="180dgc33ua6ph">
                    <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-2466" data-mp="13225pq5pzs67" />
                  </span>
                  The Average Freelancer
                </h3>
                <ul className="space-y-4" data-id="element-2467" data-mp="y4ir2p541ddq">
                  {['Cheap, bloated templates', 'No marketing or conversion strategy', 'Zero accountability for results', 'Disappears after launch'].map((item, i) => <li key={i} className="flex items-start" data-id="element-2468" data-mp="k97zplq2rae4" data-mpi={i}>
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5 opacity-70" data-id="element-2469" data-mp="fbeuauwlikew" />
                      <span className="text-gray-400" data-id="element-2470" data-mp="add9o5jiud1b">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2471" data-mp="2qy8nqlbu7c22">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-viridia/30 h-full shadow-lg shadow-viridia/5" data-id="element-2472" data-mp="2r1e8jodfnrrz">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center" data-id="element-2473" data-mp="2zzpxzn2kcjiu">
                  <span className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center mr-3" data-id="element-2474" data-mp="239peo3kyqv8x">
                    <CheckCircleIcon className="w-5 h-5 text-viridia" data-id="element-2475" data-mp="288k59rp69o3l" />
                  </span>
                  Viridia Analytics
                </h3>
                <ul className="space-y-4" data-id="element-2476" data-mp="2c8u84vho3ye8">
                  {['Conversion-focused, custom builds', 'Deep local SEO knowledge', 'Ongoing support and optimization', 'Built specifically for your market'].map((item, i) => <li key={i} className="flex items-start" data-id="element-2477" data-mp="1oigi1ain89ma" data-mpi={i}>
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0 mt-0.5" data-id="element-2478" data-mp="hya4apup8esu" />
                      <span className="text-gray-200 font-medium" data-id="element-2479" data-mp="czp8r3qe6jaq">{item}</span>
                    </li>)}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-light text-gray-900" data-id="element-2480" data-mp="10w31rcmjzwiu">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2481" data-mp="10u9ubv44989s">
          <ScrollReveal data-id="element-2482" data-mp="vwi4r7qy1y8r">
            <div className="text-center mb-16" data-id="element-2483" data-mp="vx0104dlwcgo">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2484" data-mp="1ampgz5qcyl10">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600" data-id="element-2485" data-mp="15oe74v02cais">
                Everything you need to know about our packages and billing.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-2486" data-mp="1fmadl9i22hak">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100" data-id="element-2487" data-mp="1fn3kqbkxf8t8">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2488" data-mp="238w2xgafnzdl" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia to-viridia-dark relative overflow-hidden" data-id="element-2489" data-mp="1fj1vxfcsp519">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2490" data-mp="1k9i86w4vqkm1"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-2491" data-mp="16jolokgukcfh">
          <ScrollReveal data-id="element-2492" data-mp="1akryey3llsch">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2493" data-mp="1ak8i1ltljc0k">
              Not Sure Which Package Is Right?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2494" data-mp="11j0qrnphzbfz">
              Book a free call and we'll recommend the perfect plan based on
              your business size and goals.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-white text-viridia-dark hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1" data-id="element-2495" data-mp="1xjvkpaddnwo9">
              Book Your Free Consultation
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}